import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { recoverStuckUploads, rearmNetworkExhausted, clearUploadBackoff, emitQueueSnapshot, convertPendingHeicPhotos } from "../field_image_queue"
import { compressImageWithRetry } from "../field_image_compressor"
import { logEvent, logError } from "../field_logger"
import { syncLogs } from "../field_log_sync"
import { syncCards } from "../field_card_sync"
import { syncPhotoDeletes } from "../field_photo_delete_sync"
import { syncSettings } from "../field_settings_sync"
import { getBatterySnapshot } from "../battery_monitor"
import { foregroundTimeStamp } from "../field_foreground_time"
import { writeCsrfToken } from "../field_session"
import { syncImagesAndReplay, syncOrphanedImages } from "../field_upload_sync"
import { SYNC_LOCK_NAME } from "../field_sync_shared"
import { refreshLocationForSync } from "../field_location"

// Manual sync controller for offline queue replay.
//
// Reads pending_responses from IndexedDB and replays them to the server.
// Auto-triggers when the network comes back online (field:online event).
// Shows a "Sync now" button with pending count when queue is non-empty.
//
// Usage:
//   data-controller="field-sync"
//   data-field-sync-target="button"   -- the sync button (shown/hidden by count)
//   data-field-sync-target="count"    -- the pending count badge
//   data-field-sync-target="status"   -- optional status message element
//
// Mounted twice (card #3827): on the field layout <body> with no targets, so
// every page -- including the response page, which has no shared header --
// writes the CSRF token for the service worker and drains a waiting queue on
// open (iOS has no Background Sync); and in the shared header, which owns the
// button/count/status targets. Every target is optional. On a page that has
// the header, the target-less instance defers every sync to it (one pass, and
// the header's status line still reports it); on a page without one it does
// the work itself. The drain-on-open only runs if nothing else holds
// SYNC_LOCK_NAME (another tab, the service worker).

// The "open" queue snapshot is taken once per page load, not once per
// instance -- two instances connecting together would otherwise both pass
// emitQueueSnapshot's fingerprint throttle before either had written it.
let lastOpenSnapshotAt = 0
const OPEN_SNAPSHOT_DEDUPE_MS = 5000

// Card #3844 -- the offline-to-online transition only clears the photo retry
// wait after a real outage. A signal flapping in and out every few seconds
// would otherwise clear it on every flap and defeat the backoff.
export const RECONNECT_CLEAR_MIN_OFFLINE_MS = 60_000

export default class extends Controller {
  static targets = ["button", "count", "status"]

  connect() {
    this._syncing = false
    this._clearBackoffNext = null

    // Persist CSRF token to IndexedDB for service worker access (W5)
    const csrf = document.querySelector("[name='csrf-token']")?.content
    if (csrf) writeCsrfToken(csrf).catch(() => {})

    // Recover any image left stuck mid-upload by a prior crash/force-close
    // (Card #2419). Run on app open alongside the existing purge sweeps
    // (result_editor_controller.js's connect()).
    recoverStuckUploads().catch(() => {})

    // Card #3799 -- app-open queue snapshot, so a stuck queue is visible
    // server-side from the moment the tasker opens the app, not just at the
    // end of a sync pass.
    if (Date.now() - lastOpenSnapshotAt > OPEN_SNAPSHOT_DEDUPE_MS) {
      lastOpenSnapshotAt = Date.now()
      emitQueueSnapshot("open").catch(() => {})
    }

    this._updateCount()

    // Card #3827 -- drain on open. Previously only transitions (field:online,
    // visibilitychange, a tap) started a pass, so a cold start with a waiting
    // queue did nothing on iOS until one of those happened.
    this._drainOnOpen()

    // Auto-sync when connectivity is restored
    this._onlineHandler = (event) => this.sync(event)
    window.addEventListener("field:online", this._onlineHandler)

    // Manual sync requested by the online CTA banner (W3.4). Decoupled so the
    // banner never duplicates upload logic -- it just fires the event, we sync.
    this._syncNowHandler = (event) => this.sync(event)
    window.addEventListener("field:sync-now", this._syncNowHandler)

    // Trigger sync when app returns to foreground (all platforms). Card
    // #3799 -- also snapshot the queue on the way to background, so a queue
    // left mid-sync when the tasker switches away or backgrounds the app is
    // captured rather than only ever seen on the next reopen.
    this._visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        this.sync({ type: "visibilitychange" })
      } else if (document.visibilityState === "hidden") {
        emitQueueSnapshot("hidden").catch(() => {})
      }
    }
    document.addEventListener("visibilitychange", this._visibilityHandler)
  }

  disconnect() {
    window.removeEventListener("field:online", this._onlineHandler)
    window.removeEventListener("field:sync-now", this._syncNowHandler)
    document.removeEventListener("visibilitychange", this._visibilityHandler)
  }

  // ifAvailable (card #3827): skip rather than queue behind a pass already
  // holding the lock -- used by the drain-on-open, where another instance or
  // the service worker is already doing the same work.
  async sync(event, { ifAvailable = false } = {}) {
    if (this._deferToHeader()) return
    if (localStorage.getItem("field:online") === "false") return

    // Distinguish manual taps from auto-sync (connectivity restored)
    const trigger = event?.type === "field:online"    ? "auto"
                  : event?.type === "visibilitychange" ? "reopen"
                  : event?.type === "field:sync-now"   ? "cta"
                  : event?.type === "open"             ? "open"
                  :                                      "manual"

    const clearBackoff = this._shouldClearBackoff(trigger, event)

    // A pass is already running. Its pickup queries have run, so a clear
    // asked for now would be lost: remember it, and the running pass starts
    // one more, clearing, pass as it finishes (card #3844).
    if (this._syncing) {
      if (clearBackoff) this._clearBackoffNext = trigger
      return
    }

    await this._runLocked(trigger, { clearBackoff, ifAvailable })
  }

  // -- private --

  // Card #3844 -- the photo retry wait is cleared by a tap, always: the
  // header Sync button ("manual"), and field:sync-now or field:online sent
  // with detail.userInitiated (the sync banner, the Sync page). The
  // offline-to-online transition ("auto") clears it only after at least
  // RECONNECT_CLEAR_MIN_OFFLINE_MS offline. Drain-on-open, reopen and the
  // result editor's own field:sync-now keep the backoff.
  _shouldClearBackoff(trigger, event) {
    if (trigger === "manual") return true
    if (event?.detail?.userInitiated === true) return trigger === "cta" || trigger === "auto"
    if (trigger !== "auto") return false
    const offlineMs = event?.detail?.offlineMs
    return typeof offlineMs === "number" && offlineMs >= RECONNECT_CLEAR_MIN_OFFLINE_MS
  }

  async _runLocked(trigger, { clearBackoff = false, ifAvailable = false } = {}) {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    // The actual sync work runs inside a Web Locks request (Card #2421) --
    // SYNC_LOCK_NAME is the SAME fixed name the background-sync service
    // worker requests, so a foreground tab and a background-sync event can
    // never both flush the same queued record concurrently. Browsers without
    // the Web Locks API (older Safari) just run the work directly.
    const runSync = () => this._runSync(trigger, csrfToken, { clearBackoff })

    if (navigator.locks?.request) {
      if (ifAvailable) {
        await navigator.locks.request(SYNC_LOCK_NAME, { ifAvailable: true }, lock => lock ? runSync() : null)
      } else {
        await navigator.locks.request(SYNC_LOCK_NAME, runSync)
      }
    } else {
      await runSync()
    }
  }

  // The layout instance (no targets) steps aside when the header's instance is
  // on the page, so each trigger runs one pass, reported in the header.
  _deferToHeader() {
    return !this.hasButtonTarget && !!document.querySelector("[data-field-sync-target='button']")
  }

  // Only when there is something to drain -- the layout instance connects on
  // every page, and an empty queue needs no pass (or its log/card sync calls).
  async _drainOnOpen() {
    try {
      if (await this._outstandingCount() === 0) return
      await this.sync({ type: "open" }, { ifAvailable: true })
    } catch (error) {
      logError("sync", "Sync on open failed", { name: error?.name, message: error?.message }).catch(() => {})
    }
  }

  async _runSync(trigger, csrfToken, { clearBackoff = false } = {}) {
    // Card #3798 -- recover any image left stuck "uploading" by a prior
    // crash/force-close/abort BEFORE this pass's pickup queries run (both the
    // pending_responses query below and syncImagesFor/syncOrphanedImages
    // deliberately skip "uploading" records -- Card #2419). Previously this
    // only ran once, at connect() time, outside the Web Lock -- a record that
    // got stuck mid-pass (rather than mid-app-launch) wouldn't be picked back
    // up until the app fully reloaded. Running it here, inside the SAME lock
    // every sync pass acquires, means it can never race a concurrent
    // foreground/background-sync pass touching the same record.
    await recoverStuckUploads().catch(() => {})
    // Card #3827 -- photos an older build marked failed after 5 network
    // errors go back to pending (same lock, same reasoning as above).
    await rearmNetworkExhausted().catch((error) => {
      logError("sync", "Re-arming network-stopped photos failed", { name: error?.name, message: error?.message }).catch(() => {})
    })
    // Card #4914 -- convert any HEIC photo still waiting on its decoder
    // (field_offline_app_controller.js queued the ORIGINAL file rather than
    // drop it when heic_decoder.js wasn't cached at capture time) BEFORE the
    // pickup queries below ever try to send it. This controller is page
    // context -- the only place compressImageWithRetry's <canvas>/<img>
    // decode can run (see convertPendingHeicPhotos's own doc comment for why
    // field_image_queue.js never imports the compressor itself). A photo
    // whose decoder still isn't available (still offline, secondary install
    // not landed yet) is left queued exactly as it was; this runs again next
    // sync pass.
    await convertPendingHeicPhotos(compressImageWithRetry).catch((error) => {
      logError("sync", "Converting queued HEIC photos failed", { name: error?.name, message: error?.message }).catch(() => {})
    })
    if (clearBackoff) {
      await clearUploadBackoff(trigger).catch((error) => {
        logError("sync", "Clearing photo retry wait failed", { name: error?.name, message: error?.message }).catch(() => {})
      })
    }

    // Pick up "reauth-required" alongside "pending" (Card #2417) -- a record
    // marked reauth-required after a session expiry retries automatically on
    // every subsequent sync pass. If the tasker still isn't signed back in
    // this attempt fails the same way and gets reclassified again (harmless,
    // idempotent); if they ARE signed back in, this is the attempt that
    // proves it and lets the record through.
    const pending = await db.pending_responses
      .where("status").anyOf(["pending", "reauth-required"])
      .toArray()

    // Card #3798 set _syncing in the no-pending branch too (previously only
    // the pending-responses path did), so a second sync() call arriving
    // mid-pass is turned away immediately instead of queueing redundant work.
    // Review fix (#3796/#3798) -- set ONCE here and reset in a single
    // `finally`, so a throw anywhere below (e.g. a Dexie/IndexedDB write
    // rejecting with "connection is closing" on iOS Safari) can never leave
    // _syncing stuck true. Before this, one throw silently disabled every
    // later sync() until the PWA reloaded -- which can be days on iOS.
    this._syncing = true
    try {
      await this._runSyncPass(trigger, csrfToken, pending)
    } finally {
      this._syncing = false
      this._updateCount()
      this._runDeferredClear()
    }
  }

  // A clear asked for while this pass ran (card #3844). Started after the
  // lock is released -- requesting it from inside the lock callback and
  // waiting would deadlock -- and skipped if the app has gone offline since.
  _runDeferredClear() {
    const trigger = this._clearBackoffNext
    if (!trigger) return
    this._clearBackoffNext = null
    setTimeout(() => {
      if (!this.element.isConnected || localStorage.getItem("field:online") === "false") return
      this._runLocked(trigger, { clearBackoff: true }).catch((error) => {
        logError("sync", "Follow-up sync failed", { name: error?.name, message: error?.message }).catch(() => {})
      })
    }, 0)
  }

  // The body of one sync pass. Always called from _runSync, which owns the
  // _syncing flag and its guaranteed reset -- nothing in here touches it.
  async _runSyncPass(trigger, csrfToken, pending) {
    if (pending.length === 0) {
      // No pending responses, but there may be orphaned pending images
      // (images whose response was already synced but the upload failed on a prior attempt).
      await syncOrphanedImages(csrfToken)
      // Ship any unsent logs -- happens naturally whenever the user is online
      // even if they haven't completed a response, making this a good background sync proof-of-concept.
      await syncLogs(csrfToken).catch(() => {})
      // Raise a Ticket submissions queued while offline (#2617).
      await syncCards(csrfToken).catch(() => {})
      // Photo deletes queued while offline (#4744).
      await syncPhotoDeletes(csrfToken).catch(() => {})
      // Personal settings: send queued changes and pull the server rows (#4153).
      await syncSettings({ force: true }).catch(() => {})
      // End-of-pass queue snapshot (Card #3799) -- this branch has no
      // pending responses, but there may still be an at-risk image queue.
      await emitQueueSnapshot("sync-pass").catch(() => {})
      return
    }

    this._setStatus("Syncing...")
    logEvent("sync", "Sync started", { trigger, pendingResponses: pending.length }).catch(() => {})
    // Card #4833 -- moment (d): a sync pass that actually has responses to
    // send, while online. Opportunistic cache refresh only (skips itself when
    // there's already a fix under 10 minutes old, or a remembered denial) --
    // never awaited, so a slow or failed fix never delays the sync pass.
    refreshLocationForSync().catch(() => {})
    let successCount = 0
    let failCount = 0
    let reauthCount = 0

    for (const record of pending) {
      // Upload queued images, substitute their server URLs into the survey
      // data, persist that, replay, and purge only what the replay (or the
      // server's attach at upload) confirmed -- the one shared implementation
      // the service worker also uses (field_upload_sync.js, card #3827;
      // ADR-0046: one implementation, not two). This used to purge BEFORE the
      // replay, on the strength of the local write alone.
      const result = await syncImagesAndReplay(record, csrfToken)
      if (result === "success") {
        successCount++
      } else if (result === "failed") {
        failCount++
      } else if (result === "reauth-required") {
        reauthCount++
      }
      // "retry" (network issue or 5xx) -- leave pending and move on to the
      // NEXT record rather than stopping the whole cycle (Card #2434: one
      // persistently-failing record must not block everyone else's
      // already-ready work).
    }

    // After syncing responses, catch any orphaned images that remain
    await syncOrphanedImages(csrfToken)

    // Ship diagnostic logs to the server (low-priority, best-effort)
    await syncLogs(csrfToken).catch(() => {})
    // Raise a Ticket submissions queued while offline (#2617)
    await syncCards(csrfToken).catch(() => {})
    // Photo deletes queued while offline (#4744).
    await syncPhotoDeletes(csrfToken).catch(() => {})
    // Personal settings: send queued changes and pull the server rows (#4153).
    await syncSettings({ force: true }).catch(() => {})
    // End-of-pass queue snapshot (Card #3799)
    await emitQueueSnapshot("sync-pass").catch(() => {})

    // _syncing reset + _updateCount happen in _runSync's finally, after this
    // returns. The sync_log/battery/status tail below is all best-effort.

    // Record structured sync cycle entry for the Offline Storage sync log
    const outcome = failCount > 0 && successCount === 0 ? "failed"
                  : failCount > 0                       ? "partial"
                  :                                       "success"
    await db.sync_log.add({
      kind:              "full",
      outcome,
      responsesUploaded: successCount,
      responsesFailed:   failCount,
      syncedAt:          new Date().toISOString()
    }).catch(() => {})
    // Keep the log trim -- retain last 50 entries
    const total = await db.sync_log.count().catch(() => 0)
    if (total > 50) {
      const oldest = await db.sync_log.orderBy("syncedAt").limit(total - 50).primaryKeys().catch(() => [])
      await db.sync_log.bulkDelete(oldest).catch(() => {})
    }

    // Log battery state after sync for drain correlation
    getBatterySnapshot().then(snap => {
      if (snap) logEvent("battery", "Battery after sync", { level: snap.level, charging: snap.charging, syncOutcome: outcome, ...foregroundTimeStamp() }).catch(() => {})
    }).catch(() => {})

    if (reauthCount > 0) {
      logEvent("sync", "Sync paused pending reauthentication", { trigger, reauthCount }).catch(() => {})
      this._setStatus("Sign in again to finish syncing")
    } else if (failCount > 0) {
      logError("sync", "Sync completed with failures", { trigger, successCount, failCount }).catch(() => {})
      this._setStatus(`${failCount} record${failCount > 1 ? "s" : ""} failed validation -- tap to review`)
    } else if (successCount > 0) {
      logEvent("sync", "Sync completed", { trigger, successCount }).catch(() => {})
      this._setStatus(`${successCount} record${successCount > 1 ? "s" : ""} synced`)
      setTimeout(() => this._setStatus(null), 3000)
    }

    if (successCount > 0 || failCount > 0) {
      window.dispatchEvent(new CustomEvent("field:sync-complete", { detail: { successCount, failCount } }))
    }
  }

  // Count "reauth-required" alongside "pending" -- it's still outstanding
  // work waiting to sync, not a resolved state (Card #2417).
  async _outstandingCount() {
    const responseCount = await db.pending_responses
      .where("status").anyOf(["pending", "reauth-required"])
      .count()
    const imageCount = await db.pending_images
      .where("status").anyOf(["pending", "reauth-required"])
      .count()
    return responseCount + imageCount
  }

  async _updateCount() {
    // No targets (the layout instance) -- nothing to paint.
    if (!this.hasCountTarget && !this.hasButtonTarget) return
    const total = await this._outstandingCount()

    if (this.hasCountTarget) {
      this.countTarget.textContent = total > 0 ? total : ""
    }

    if (this.hasButtonTarget) {
      if (total > 0) {
        this.buttonTarget.classList.remove("hidden")
      } else {
        this.buttonTarget.classList.add("hidden")
      }
    }
  }

  _setStatus(message) {
    if (!this.hasStatusTarget) return
    if (message) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.remove("hidden")
    } else {
      this.statusTarget.classList.add("hidden")
    }
  }

}
